import { get, patch, post } from './api';
import { uploadToGCS } from './uploads';
import type {
  BulkCalendarEventsReviewResponse,
  ConfirmBulkCalendarEventsRequest,
  ConfirmBulkCalendarEventsResponse,
  ConfirmScheduleIngestionRequest,
  ConfirmScheduleIngestionResponse,
  ConfirmScheduleIngestionResult,
  CreateScheduleIngestionRequest,
  CreateScheduleIngestionResponse,
  ReextractionQueued,
  ScheduleFetchMode,
  StagedGameEdit,
  ScheduleIngestionKind,
  ScheduleIngestionReviewResponse,
  ScheduleIngestionStatusResponse,
  ScheduleMergeResultResponse,
  ScheduleSourceType,
  StartScheduleIngestionResponse,
} from '../types/schedule-ingestion';
import type { RecentScheduleIngestionDto } from '../types/build-itinerary';
import { EXTRACTION_TIMEOUT_MS } from '../constants/extraction';

const DEFAULT_RECENT_TAKE = 20;

export async function createScheduleIngestion(
  body: CreateScheduleIngestionRequest,
  signal?: AbortSignal
): Promise<CreateScheduleIngestionResponse> {
  return post<CreateScheduleIngestionResponse>(
    '/api/schedule-ingestions',
    body,
    { signal }
  );
}

export async function startScheduleIngestion(
  ingestionId: string,
  signal?: AbortSignal
): Promise<StartScheduleIngestionResponse> {
  return post<StartScheduleIngestionResponse>(
    `/api/schedule-ingestions/${ingestionId}/start`,
    undefined,
    { timeout: EXTRACTION_TIMEOUT_MS, signal }
  );
}

export async function getScheduleIngestionStatus(
  ingestionId: string,
  signal?: AbortSignal,
  timeout?: number
): Promise<ScheduleIngestionStatusResponse> {
  return get<ScheduleIngestionStatusResponse>(
    `/api/schedule-ingestions/${ingestionId}/status`,
    { signal, timeout }
  );
}

/**
 * Load the editable review payload for a completed ingestion: the extracted
 * games plus the server's team-name matches. When
 * `resultCalendarEventId` is non-null the ingestion is already confirmed.
 */
export async function getScheduleIngestionReview(
  ingestionId: string,
  signal?: AbortSignal
): Promise<ScheduleIngestionReviewResponse> {
  return get<ScheduleIngestionReviewResponse>(
    `/api/schedule-ingestions/${ingestionId}/review`,
    { signal }
  );
}

/**
 * Confirm a reviewed ingestion. Two server paths, one endpoint:
 *
 * - CREATE (no `targetCalendarEventId`): creates a new tournament, its teams, and
 *   games. The server returns HTTP 201 with the legacy `ConfirmScheduleIngestionResponse`.
 * - MERGE / rebuild (`targetCalendarEventId` set): diffs the re-imported games into
 *   the existing tournament and returns HTTP 200 with `ScheduleMergeResultResponse`.
 *
 * We route the response shape off the same signal the server routes on: whether a
 * target event was sent. The caller gets a discriminated result so it never has to
 * guess which shape came back (and old create-only callers keep working via
 * {@link confirmScheduleIngestion}).
 */
export async function confirmScheduleIngestionWithResult(
  ingestionId: string,
  body: ConfirmScheduleIngestionRequest,
  signal?: AbortSignal
): Promise<ConfirmScheduleIngestionResult> {
  const isMerge = Boolean(body.targetCalendarEventId);
  if (isMerge) {
    const result = await post<ScheduleMergeResultResponse>(
      `/api/schedule-ingestions/${ingestionId}/confirm`,
      body,
      { signal }
    );
    return { kind: 'merged', result };
  }
  const response = await post<ConfirmScheduleIngestionResponse>(
    `/api/schedule-ingestions/${ingestionId}/confirm`,
    body,
    { signal }
  );
  return { kind: 'created', response };
}

/**
 * Confirm a reviewed ingestion on the CREATE path only: creates the tournament
 * calendar event, any missing teams, and the games under it. Returns the created
 * tournament summary. Used by the Build Itinerary wizard (which never merges).
 * For the rebuild path (and to handle both shapes), use
 * {@link confirmScheduleIngestionWithResult}.
 */
export async function confirmScheduleIngestion(
  ingestionId: string,
  body: ConfirmScheduleIngestionRequest,
  signal?: AbortSignal
): Promise<ConfirmScheduleIngestionResponse> {
  return post<ConfirmScheduleIngestionResponse>(
    `/api/schedule-ingestions/${ingestionId}/confirm`,
    body,
    { signal }
  );
}

/**
 * List the org's most recent schedule imports (newest first) for the Build
 * Itinerary wizard's "Recent events / Import again" list. Org-scoped server-side.
 *
 * `take` bounds the page size; it defaults to 20 to match the backend default.
 */
export async function getRecentIngestions(
  take: number = DEFAULT_RECENT_TAKE,
  signal?: AbortSignal
): Promise<RecentScheduleIngestionDto[]> {
  return get<RecentScheduleIngestionDto[]>(
    `/api/schedule-ingestions/recent?take=${take}`,
    { signal }
  );
}

/**
 * Decide the source type the backend expects from a file. PDFs are "Pdf",
 * everything else we accept (images) is "Image".
 */
function sourceTypeForFile(file: File): ScheduleSourceType {
  const isPdf =
    file.type === 'application/pdf' ||
    file.name.toLowerCase().endsWith('.pdf');
  return isPdf ? 'Pdf' : 'Image';
}

function fileExtension(file: File): string {
  const parts = file.name.split('.');
  return parts.length > 1 ? `.${parts.pop()!.toLowerCase()}` : '';
}

/**
 * Convenience helper: create an ingestion, upload the file to GCS, and return
 * the ingestion id ready to be started. Mirrors uploadRosterFile in uploads.ts.
 */
export async function uploadScheduleFile(
  file: File,
  signal?: AbortSignal
): Promise<string> {
  const { ingestionId, uploadUrl } = await createScheduleIngestion(
    {
      sourceType: sourceTypeForFile(file),
      fileExtension: fileExtension(file),
      contentType: file.type || undefined,
    },
    signal
  );

  await uploadToGCS(file, uploadUrl);

  return ingestionId;
}

/**
 * Upload one OR MORE files into a single ingestion (import several PDFs/images as one schedule).
 * Creates one ingestion, then uploads each file to its own signed URL. Returns the ingestion id
 * ready to be started. A single file uses the legacy single-file request shape so it still works
 * against older servers; multiple files use the `files` request (needs the multi-file server).
 */
export async function uploadScheduleFiles(
  files: File[],
  signal?: AbortSignal,
  kind?: ScheduleIngestionKind
): Promise<string> {
  if (files.length === 0) {
    throw new Error('No files to upload.');
  }
  // The single-file shortcut exists for servers predating the multi-file request. A kind postdates
  // both, so taking that path would drop it and silently produce a tournament ingestion.
  if (files.length === 1 && !kind) {
    return uploadScheduleFile(files[0], signal);
  }

  // A mixed batch is typed by the first PDF present (else Image); the server decides per-file by
  // extension anyway, so this only sets the coarse source label.
  const anyPdf = files.some((f) => sourceTypeForFile(f) === 'Pdf');
  const response = await createScheduleIngestion(
    {
      sourceType: anyPdf ? 'Pdf' : 'Image',
      ...(kind ? { kind } : {}),
      files: files.map((f) => ({
        fileExtension: fileExtension(f),
        contentType: f.type || undefined,
      })),
    },
    signal
  );

  // Prefer the per-file targets; fall back to the single target (defensive against an older server).
  const targets =
    response.uploads && response.uploads.length > 0
      ? response.uploads
      : [{ uploadUrl: response.uploadUrl, gcsObjectName: response.gcsObjectName }];

  // An older server answers a multi-file request with one target. Indexing past the end would be a
  // bare "Cannot read properties of undefined", reported to the coach as "we could not read that
  // file" — which sends them off re-scanning a document that was never the problem.
  if (targets.length !== files.length) {
    throw new Error(
      'This server cannot accept several files in one schedule. Upload them one at a time.'
    );
  }

  await Promise.all(
    files.map((file, i) => uploadToGCS(file, targets[i].uploadUrl))
  );

  return response.ingestionId;
}

/**
 * Convenience helper for the URL source: create an ingestion from a pasted
 * schedule link and return the ingestion id. The server enqueues the job
 * immediately (status "Pending"), so unlike the file flow there is no upload
 * and no /start call. The caller just polls status. A malformed or unsafe URL
 * makes the create call reject (HTTP 400) with a message to show the user.
 */
export async function startScheduleIngestionFromUrl(
  sourceUrl: string,
  signal?: AbortSignal,
  fetchMode?: ScheduleFetchMode
): Promise<string> {
  const { ingestionId } = await createScheduleIngestion(
    {
      sourceType: 'Url',
      sourceUrl,
      // Omitted means the server's default (a plain HTTP GET), so every caller
      // that predates rendering keeps its exact behaviour.
      ...(fetchMode ? { fetchMode } : {}),
    },
    signal
  );

  return ingestionId;
}

/**
 * Load the extracted events for a bulk (`CalendarEvents`) ingestion, for the coach to review before
 * any of them land on a calendar. Rejects with a 400 when the ingestion was read as a tournament.
 */
export async function getBulkCalendarEventsReview(
  ingestionId: string,
  signal?: AbortSignal,
  timeout?: number
): Promise<BulkCalendarEventsReviewResponse> {
  return get<BulkCalendarEventsReviewResponse>(
    `/api/schedule-ingestions/${ingestionId}/calendar-events`,
    { signal, timeout }
  );
}

/**
 * Create the reviewed events on the calendar, in one transaction. All-or-nothing server-side: a
 * coach who reviewed twelve events and received seven has no way to tell which five are missing.
 *
 * A second call for the same ingestion is a 409, which is the guard against a retried request
 * putting every event on the calendar twice.
 */
export async function confirmBulkCalendarEvents(
  ingestionId: string,
  body: ConfirmBulkCalendarEventsRequest,
  signal?: AbortSignal
): Promise<ConfirmBulkCalendarEventsResponse> {
  return post<ConfirmBulkCalendarEventsResponse>(
    `/api/schedule-ingestions/${ingestionId}/confirm-calendar-events`,
    body,
    { signal }
  );
}

/**
 * Upload one or more documents as a BULK EVENTS ingestion and return the ingestion id, ready to be
 * started.
 *
 * A named function rather than a `kind` argument at the call site: that single field decides which
 * prompt the server sends and which confirm endpoint the result can go through, so a caller that
 * got it wrong would upload successfully and then be unable to confirm. The name carries the
 * meaning; the body is shared, because two copies of the signed-URL fan-out is how they drift.
 */
export async function uploadBulkEventFiles(
  files: File[],
  signal?: AbortSignal
): Promise<string> {
  return uploadScheduleFiles(files, signal, 'CalendarEvents');
}

/**
 * Save edits to the games staged for review.
 *
 * Returns the rebuilt review rather than an acknowledgement: an edited team NAME changes which
 * existing teams the server suggests, so the caller needs the new suggestions, not just confirmation
 * that the write landed.
 *
 * Until this existed the review table's edits went nowhere — they were held in React state that the
 * confirm handler never read, and the confirm request carries no games.
 */
export async function updateStagedGames(
  ingestionId: string,
  games: StagedGameEdit[],
  signal?: AbortSignal
): Promise<ScheduleIngestionReviewResponse> {
  return patch<ScheduleIngestionReviewResponse>(
    `/api/schedule-ingestions/${ingestionId}/games`,
    { games },
    { signal }
  );
}

/**
 * Re-read the SAME page with a correction applied.
 *
 * The page text from the last run is kept server-side, so this costs one model call and a few
 * seconds rather than a browser and a fresh page load — which is what makes iterating on a bad
 * extraction worth doing more than once.
 *
 * Returns once the replay is QUEUED (HTTP 202). The caller polls status as it would for any run.
 */
export async function reextractSchedule(
  ingestionId: string,
  feedback: string,
  signal?: AbortSignal
): Promise<ReextractionQueued> {
  return post<ReextractionQueued>(
    `/api/schedule-ingestions/${ingestionId}/reextract`,
    { feedback },
    { signal }
  );
}
